import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import styles from "./CommunityDetailPage.module.css";
import CommunitySettingsModal from "../../Components/CommunitySettingsModal/CommunitySettingsModal";
import FollowButton from "../../Components/FollowButton/FollowButton";
import WorkspaceList from "../../Components/WorkspaceList/WorkspaceList";
import ProjectRow from "../../Components/ProjectRow/ProjectRow";
import CreateWorkspaceModal from "../../Components/CreateWorkspaceModal/CreateWorkspaceModal";
import CommunityMvvSection from "../../Components/CommunityMvvSection/CommunityMvvSection";
import {
  getCommunityOverride,
  setCommunityOverride,
} from "../../Data/CommunityConfig/communityConfigOverrides";
import { uploadDataUrl, fileUrl } from "../../api/fileStorage";
import { mediaSrc } from "../../api/mediaUrl";
import { communityConfigStore } from "../../Data/store";
import { resolveCommunityPathNames, resolveCommunityAncestors, refreshCommunityMaps } from "../../Data/ProjectConfig/projectConfigOverrides";
import { useApiCommunityData } from "../../api/useApiCommunityData";
import { parseTabVisibility, resolveEffectiveLogoUrl, updateCommunityEntity } from "../../api/communityMetadata";
import CircleCropModal from "../../Components/CircleCropModal/CircleCropModal";
import CommunityEnablers from "../../Components/CommunityEnablers/CommunityEnablers";
import CommunityNewsFeed from "../../Components/CommunityNewsFeed/CommunityNewsFeed";
import CommunityFeed from "../../Components/CommunityFeed/CommunityFeed";
import CommunityHealthTab from "../../Components/CommunityHealthTab/CommunityHealthTab";
import CommunityInfoCard from "../../Components/CommunityInfoCard/CommunityInfoCard";
import ProgressGallery from "../../Components/ProgressGallery/ProgressGallery";
import { hydrateEnablersFromApi } from "../../Data/CommunityConfig/communityEnablers";

// DEV-932: flatfile imports removed. communityEntities and
// communityRelationships are read from useApiCommunityData (already in
// scope below). securityUsers and communityRoles read from
// useApiSecurityData. The localStorage override layer
// (getCommunityRoleOverrides) is gone — the DB is the only source of
// truth for role assignments.
import { useApiSecurityData } from "../../api/useApiSecurityData";
// DEV-1190: Community Management tab — operational admin peer to News/Feed/etc.
import CommunityManagementTab from "../../Components/CommunityManagementTab/CommunityManagementTab";
import CommunityPlaybooksTab from "../../Components/CommunityPlaybooksTab/CommunityPlaybooksTab";
// Onboarding chunk A (DEV-1303, §7.9) — invited-Explorer welcome nudge +
// the durable home of the org's designated guidelines.
import WelcomeNudge from "../../Components/OnboardingWelcome/WelcomeNudge";
import CommunityGuidelinesSection from "../../Components/OnboardingWelcome/CommunityGuidelinesSection";
// Voice-ladder gate (Skip 2026-07-07): Explorer posting is org-scoped and
// standards/guidelines-gated; mirrors the server check in NewsController.Share.
import { useApiOnboarding } from "../../api/useApiOnboarding";
import { useApiCommunityGuidelines } from "../../api/useApiCommunityGuidelines";
import { STANDARDS_CURRENT_VERSION } from "../../content/legal/standardsConstants";

type CommunityEntity = {
  entity_guid: string;
  name?: string;
  entity_type?: string;
  code?: string;
  hero_image_url?: string;
  /** DEV-890 — drives the lock icon + private banner. */
  visibility?: string;
  /** DEV-968 — community info card slot 1 (free text address). */
  address?: string | null;
  /** DEV-968 — community info card slot 2 icon key (no `ti-` prefix). */
  info_icon_key?: string | null;
  /** DEV-968 — community info card slot 2 freeform text. */
  info_text?: string | null;
  /** DEV-980 — per-tab visibility flags (raw JSON string). */
  tab_visibility?: string | null;
  /**
   * DEV-1013 — hero primary-location row. References a geographic community
   * entity (city/town); the hero resolves it to a leaf-to-root chain like
   * "Hampton, Virginia, United States". NULL = no row rendered.
   */
  primary_location_entity_guid?: string | null;
  /**
   * DEV-1190 — Management tab visibility tier. One of 'public' | 'community'
   * | 'team'. Defaults to 'community' (set in DB migration 209). Capped at
   * the row's own `visibility` by the PATCH endpoint.
   */
  management_visibility?: string | null;
};

type CommunityRelationship = {
  child_entity_guid: string;
  parent_entity_guid: string;
};

/* ------------------------------------------------------------------ */
/*  Country flag resolution                                            */
/* ------------------------------------------------------------------ */

/** Convert an ISO 3166-1 alpha-2 country code to its flag emoji. */
function countryFlagEmoji(code: string | null | undefined): string {
  if (!code || code.length < 2) return '';
  const upper = code.toUpperCase().trim();
  const cc = upper.indexOf('-') === 2 ? upper.slice(0, 2) : (upper.length === 2 ? upper : '');
  if (!cc || !/^[A-Z]{2}$/.test(cc)) return '';
  return String.fromCodePoint(...Array.from(cc).map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

/**
 * Resolve country flag + local geography name for an entity.
 * The **flag** comes from the ancestor geo_country, but the **name** is
 * the starting entity itself (e.g. "Boston"), not the country.
 */
function resolveCountryForEntity(
  entityGuid: string,
  entityMap: Map<string, CommunityEntity>,
  parentMap: Map<string, string>,
): { flag: string; name: string } {
  const empty = { flag: '', name: '' };
  if (!entityGuid) return empty;
  const startGuid = entityGuid.toUpperCase();

  // Capture the starting entity's name — that's the label we display
  const startEntity = entityMap.get(startGuid);
  const localName = startEntity ? String(startEntity.name || '') : '';

  // Walk up to find the country flag
  let guid: string | undefined = startGuid;
  for (let i = 0; i < 6 && guid; i++) {
    const entity = entityMap.get(guid);
    if (!entity) break;
    const etype = String(entity.entity_type || '').toLowerCase();
    const code = entity.code;
    if (etype === 'geo_country' && code) {
      const flag = countryFlagEmoji(code);
      return { flag, name: localName || String(entity.name || code) };
    }
    guid = parentMap.get(guid);
  }
  return empty;
}

// DEV-1190: 'management' is the FIRST top-level tab, matching the project
// pattern. Visibility gated by community_entities.management_visibility
// (public | community | team).
type TabKey = "management" | "alignment" | "playbooks" | "news" | "feed" | "health" | "forum" | "media";

interface Props {
  communityGuid: string;
  currentUserGuid?: string;
}

const LS_SELECTED_COMMUNITY_KEY = "l4e_selected_community_guid";

/* ------------------------------------------------------------------ */
/*  Community hero image constants                                     */
/* ------------------------------------------------------------------ */
const COMMUNITY_MAX_RAW_FILE_BYTES = 10 * 1024 * 1024;
const COMMUNITY_JPEG_QUALITY = 0.92;
const COMMUNITY_HERO_HEIGHT = 220;
const COMMUNITY_CROP_OUTPUT_W = 1920;

/* ------------------------------------------------------------------ */
/*  CommunityCropOverlay                                               */
/* ------------------------------------------------------------------ */
interface CommunityCropState {
  src: string;
  naturalW: number;
  naturalH: number;
}

function CommunityCropOverlay({
  crop,
  onConfirm,
  onCancel,
}: {
  crop: CommunityCropState;
  onConfirm: (dataUrl: string) => void;
  onCancel: () => void;
}) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [viewportW, setViewportW] = useState(400);
  const [viewportH, setViewportH] = useState(COMMUNITY_HERO_HEIGHT);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ startX: number; startY: number; startPanX: number; startPanY: number } | null>(null);

  const computedTargetAspect = useMemo(() => {
    const parent = overlayRef.current?.parentElement;
    if (parent && parent.clientWidth > 0 && parent.clientHeight > 0) {
      return parent.clientWidth / parent.clientHeight;
    }
    return undefined;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewportW]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        setViewportW(e.contentRect.width);
        setViewportH(e.contentRect.height);
      }
    });
    ro.observe(el);
    setViewportW(el.clientWidth);
    setViewportH(el.clientHeight || COMMUNITY_HERO_HEIGHT);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const { naturalW, naturalH } = crop;
  const baseScale = Math.max(viewportW / naturalW, viewportH / naturalH);
  const displayScale = baseScale * zoom;
  const displayW = naturalW * displayScale;
  const displayH = naturalH * displayScale;

  const clampPan = useCallback(
    (px: number, py: number, z?: number) => {
      const ds = baseScale * (z ?? zoom);
      const dw = naturalW * ds;
      const dh = naturalH * ds;
      const maxPanX = Math.max(0, (dw - viewportW) / 2);
      const maxPanY = Math.max(0, (dh - viewportH) / 2);
      return {
        x: Math.max(-maxPanX, Math.min(maxPanX, px)),
        y: Math.max(-maxPanY, Math.min(maxPanY, py)),
      };
    },
    [baseScale, naturalW, naturalH, viewportW, viewportH, zoom],
  );

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, startPanX: pan.x, startPanY: pan.y };
  }, [pan]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setPan(clampPan(dragRef.current.startPanX + dx, dragRef.current.startPanY + dy));
  }, [clampPan]);

  const onPointerUp = useCallback(() => { dragRef.current = null; }, []);

  const onWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const next = Math.max(1, Math.min(4, zoom - e.deltaY * 0.002));
    setZoom(next);
    setPan((p) => clampPan(p.x, p.y, next));
  }, [zoom, clampPan]);

  const handleZoomSlider = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const next = parseFloat(e.target.value);
    setZoom(next);
    setPan((p) => clampPan(p.x, p.y, next));
  }, [clampPan]);

  const handleConfirm = useCallback(() => {
    const aspect = computedTargetAspect && computedTargetAspect > 0 ? computedTargetAspect : viewportW / viewportH;
    const visSrcW = viewportW / displayScale;
    const visSrcH = viewportH / displayScale;
    const visSrcCX = (displayW / 2 - pan.x) / displayScale;
    const visSrcCY = (displayH / 2 - pan.y) / displayScale;

    let srcW: number, srcH: number;
    if (visSrcW / visSrcH > aspect) { srcW = visSrcW; srcH = visSrcW / aspect; }
    else { srcH = visSrcH; srcW = visSrcH * aspect; }

    const srcX = Math.max(0, Math.min(naturalW - srcW, visSrcCX - srcW / 2));
    const srcY = Math.max(0, Math.min(naturalH - srcH, visSrcCY - srcH / 2));

    const outW = COMMUNITY_CROP_OUTPUT_W;
    const outH = Math.round(COMMUNITY_CROP_OUTPUT_W / aspect);
    const canvas = document.createElement('canvas');
    canvas.width = outW; canvas.height = outH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, outW, outH);
      onConfirm(canvas.toDataURL('image/jpeg', COMMUNITY_JPEG_QUALITY));
    };
    img.src = crop.src;
  }, [crop.src, naturalW, naturalH, displayW, displayH, displayScale, pan, viewportW, viewportH, computedTargetAspect, onConfirm]);

  const imgStyle: React.CSSProperties = {
    position: 'absolute', width: displayW, height: displayH,
    left: (viewportW - displayW) / 2 + pan.x, top: (viewportH - displayH) / 2 + pan.y,
    userSelect: 'none', pointerEvents: 'none',
  };

  const { t } = useAppTranslation('pages');
  return (
    <div ref={overlayRef} style={{ position: 'absolute', inset: 0, zIndex: 20, background: 'var(--bg-elevated)', display: 'flex', flexDirection: 'column', borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ padding: '8px 12px', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', background: 'var(--bg-elevated)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>{t('projectDetail.dragToReposition')}</span>
        <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{Math.round(zoom * 100)}%</span>
      </div>
      <div ref={viewportRef} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onWheel={onWheel}
        style={{ flex: 1, position: 'relative', overflow: 'hidden', cursor: dragRef.current ? 'grabbing' : 'grab', touchAction: 'none' }}>
        <img src={crop.src} alt="Crop preview" draggable={false} style={imgStyle} />
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1 }}>
          {[1, 2].map((i) => (
            <React.Fragment key={i}>
              <div style={{ position: 'absolute', left: `${(i / 3) * 100}%`, top: 0, bottom: 0, width: 1, background: 'var(--border-default)' }} />
              <div style={{ position: 'absolute', top: `${(i / 3) * 100}%`, left: 0, right: 0, height: 1, background: 'var(--border-default)' }} />
            </React.Fragment>
          ))}
        </div>
      </div>
      <div style={{ padding: '8px 12px', background: 'var(--bg-elevated)', display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 11, color: 'var(--text-tertiary)', flexShrink: 0 }}>Zoom</span>
        <input type="range" min="1" max="4" step="0.01" value={zoom} onChange={handleZoomSlider} style={{ flex: 1, accentColor: 'var(--accent-primary)', cursor: 'pointer' }} />
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" onClick={onCancel} style={{ background: 'var(--bg-hover)', border: '1px solid var(--border-default)', borderRadius: 6, color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600, padding: '5px 14px', cursor: 'pointer' }}>Cancel</button>
          <button type="button" onClick={handleConfirm} style={{ background: 'var(--color-success)', border: '1px solid var(--border-default)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 12, fontWeight: 600, padding: '5px 14px', cursor: 'pointer' }}>Save Crop</button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Community primary location — DEV-1013                              */
/* ------------------------------------------------------------------ */

/**
 * Renders the community's primary physical location below the subcommunity
 * path on the hero. Walks ancestors from the location entity upward so a
 * Rotary club whose primary location is "Hampton" renders as
 * "Hampton, Virginia, United States" with a map-pin icon.
 *
 * Returns null when no GUID is set or the target entity can't be resolved
 * yet (graceful degradation — the breadcrumb above this row already stands
 * on its own).
 *
 * Skip's mockup-session choice (2026-05-12, variant A): name → subcommunity
 * path → primary location.
 */
function CommunityPrimaryLocation({
  primaryLocationGuid,
  onNavigate,
}: {
  primaryLocationGuid?: string | null;
  onNavigate?: (guid: string) => void;
}) {
  const guid = String(primaryLocationGuid || '').trim();
  if (!guid) return null;

  const upper = guid.toUpperCase();
  const locEntity = communityConfigStore.get(upper);
  const leafName = locEntity?.name as string | undefined;
  if (!leafName) return null;

  const ancestors = resolveCommunityAncestors(guid);
  const chain: { guid: string; name: string }[] = [{ guid, name: leafName }];
  for (let i = ancestors.length - 1; i >= 0; i--) {
    chain.push(ancestors[i]);
  }

  return (
    <div className={`${styles.metaRow} ${styles.locationRow}`}>
      <span className={styles.locationIcon} aria-hidden="true">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
      </span>
      {chain.map((a, i) => {
        const entity = communityConfigStore.get(a.guid.toUpperCase());
        const isActive = entity?.is_active !== false;
        return (
          <React.Fragment key={a.guid}>
            {i > 0 && <span className={styles.breadcrumbSep}>{', '}</span>}
            {isActive && onNavigate ? (
              <button
                type="button"
                className={styles.breadcrumbLink}
                onClick={() => onNavigate(a.guid)}
              >
                {a.name}
              </button>
            ) : (
              <span className={styles.breadcrumbText}>{a.name}</span>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Community breadcrumb — "Massachusetts, United States"               */
/* ------------------------------------------------------------------ */

function CommunityBreadcrumb({ communityGuid, communityName, onNavigate }: { communityGuid: string; communityName: string; onNavigate?: (guid: string) => void }) {
  // resolveCommunityAncestors returns root-to-leaf { guid, name } pairs,
  // excluding "Earth" and the current community.
  const ancestors = resolveCommunityAncestors(communityGuid);

  if (ancestors.length === 0) return null;

  // Reverse to leaf-to-root: "Massachusetts, United States"
  const reversed = [...ancestors].reverse();

  return (
    <div className={styles.metaRow}>
      {reversed.map((a, i) => {
        // Check if this ancestor community is active (has content/owner)
        const entity = communityConfigStore.get(a.guid.toUpperCase());
        const isActive = entity?.is_active !== false;

        return (
          <React.Fragment key={a.guid}>
            {i > 0 && <span className={styles.breadcrumbSep}>{',\u00A0'}</span>}
            {isActive && onNavigate ? (
              <button
                type="button"
                className={styles.breadcrumbLink}
                onClick={() => onNavigate(a.guid)}
              >
                {a.name}
              </button>
            ) : (
              <span className={styles.breadcrumbText}>{a.name}</span>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

// DEV-969 — tab order: Alignment first (orientation), then News (curated),
// Feed (live stream), Health (vitals), Forum (discussion), Media (gallery).
// First-time visitors land on Alignment; returning visitors land on Feed.
// DEV-1190: Management leads, matching project pattern.
const VALID_TABS: TabKey[] = ["management", "alignment", "playbooks", "news", "feed", "health", "forum", "media"];
const DEFAULT_TAB: TabKey = "feed";

/** localStorage key prefix for tracking first visits per community. */
const VISITED_KEY_PREFIX = 'l4e_community_visited_';

const CommunityDetailPage: React.FC<Props> = ({ communityGuid, currentUserGuid }) => {
  const { t } = useAppTranslation('pages');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Persist active tab in the URL hash so browser back/forward restores it.
  // First-visit users (no localStorage flag for this community) land on Alignment;
  // returning users default to Feed.
  const hashTab = location.hash.replace('#', '') as TabKey;
  // DEV-1443: namespace the first-visit flag by user so one user's "visited"
  // state on a shared browser doesn't govern another's default tab.
  const visitedKey = `${VISITED_KEY_PREFIX}${communityGuid}:${(currentUserGuid || 'anon').toUpperCase()}`;
  const isFirstVisit = !localStorage.getItem(visitedKey);

  const resolvedDefault: TabKey = (() => {
    // Explicit hash always wins (e.g. back button from chronicle)
    if (VALID_TABS.includes(hashTab)) return hashTab;
    // First-time visitor → Alignment orientation
    if (isFirstVisit) return 'alignment';
    // Returning visitor → Feed engagement
    return DEFAULT_TAB;
  })();

  const [tab, setTabState] = useState<TabKey>(resolvedDefault);

  // Mark community as visited once the page renders
  useEffect(() => {
    if (isFirstVisit) {
      localStorage.setItem(visitedKey, '1');
    }
  }, [visitedKey, isFirstVisit]);

  const setTab = useCallback((next: TabKey) => {
    setTabState(next);
    // Replace the hash without pushing a new history entry
    window.history.replaceState(null, '', `${location.pathname}#${next}`);
  }, [location.pathname]);

  // Sync tab if the hash changes externally (e.g. browser back button).
  //
  // BUGFIX (DEV-973): the deps array must NOT include `tab`. setTab calls
  // window.history.replaceState which updates the URL bar but does NOT
  // notify react-router's useLocation hook. If `tab` is in the deps,
  // every user click fires this effect; useLocation still returns the
  // stale hash (e.g. '#forum'), the mismatch check trips, and we revert
  // the tab the user just clicked. With `tab` removed, this effect only
  // fires on a genuine external hash change (back/forward button), which
  // is the intended behavior.
  useEffect(() => {
    const ht = location.hash.replace('#', '') as TabKey;
    if (VALID_TABS.includes(ht) && ht !== tab) {
      setTabState(ht);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  const [settingsOpen, setSettingsOpen] = useState(false);
  // Slice E′ (§7.5a) — one-shot deep link from the Memberships rail's
  // "Create a community" chooser: open Settings on arrival (child-org
  // creation lives in Settings → Entities, the canonical taxonomy-aware
  // surface). State cleared immediately so back/forward never re-triggers.
  const openSettingsOneShot = !!(location.state as any)?.openCommunitySettings;
  useEffect(() => {
    if (!openSettingsOneShot) return;
    setSettingsOpen(true);
    navigate(location.pathname + location.hash, { replace: true, state: null });
  }, [openSettingsOneShot, navigate, location.pathname, location.hash]);
  const [createWorkspaceOpen, setCreateWorkspaceOpen] = useState(false);
  const [workspaceRefreshKey, setWorkspaceRefreshKey] = useState(0);

  // Fetch ALL community entities + relationships from API (for flag resolution)
  const apiCommunityData = useApiCommunityData();

  // Hydrate enabler role slots from API (falls back to demo seed if API unreachable)
  useEffect(() => { hydrateEnablersFromApi(communityGuid); }, [communityGuid]);

  // Bump when community config store is hydrated or updated so memos re-compute
  const [storeVersion, setStoreVersion] = useState(0);
  useEffect(() => {
    const bump = () => setStoreVersion((v) => v + 1);
    window.addEventListener('l4e_community_config_changed', bump);
    return () => window.removeEventListener('l4e_community_config_changed', bump);
  }, []);

  // DEV-883: Ensure community-ancestor lookup maps include the live API data.
  // App.tsx refreshes these maps as a side-effect of effectiveProjects, but a
  // user landing directly on a sub-community page (e.g. Hampton Rotary Club —
  // exists in the DB via seed_rotary_clubs.sql but not in the legacy static
  // seed) may render before that side-effect fires. Calling here makes the
  // breadcrumb work for DB-only entities. The function is additive/idempotent.
  useEffect(() => {
    const ents = apiCommunityData.communityEntities;
    const rels = apiCommunityData.communityRelationships;
    if ((ents && ents.length) || (rels && rels.length)) {
      refreshCommunityMaps(ents || [], rels || []);
    }
  }, [apiCommunityData.communityEntities, apiCommunityData.communityRelationships]);

  const community: CommunityEntity | undefined = useMemo(() => {
    // Prefer API-hydrated store (has all communities from DB)
    void storeVersion; // react to store hydration
    const normGuid = String(communityGuid).toUpperCase();
    const storeEntry = communityConfigStore.get(normGuid);
    if (storeEntry) {
      return {
        entity_guid: communityGuid,
        name: storeEntry.name,
        entity_type: storeEntry.entity_type,
        visibility: storeEntry.visibility,
        hero_image_url: storeEntry.hero_image_url,
        // DEV-968 — info card fields from the in-memory store.
        address: (storeEntry as any).address ?? null,
        info_icon_key: (storeEntry as any).info_icon_key ?? null,
        info_text: (storeEntry as any).info_text ?? null,
        // DEV-980 — tab visibility flags from the in-memory store.
        tab_visibility: (storeEntry as any).tab_visibility ?? null,
        // DEV-1013 — primary location for the hero address row.
        primary_location_entity_guid:
          (storeEntry as any).primary_location_entity_guid ?? null,
      } as CommunityEntity;
    }
    // Fallback to live API data (handles the window before the store has
    // hydrated — DEV-883). Newly-seeded communities (e.g. Rotary clubs
    // from seed_rotary_clubs.sql) live in the API but not in the legacy
    // static seed file; without this fallback they render as the generic
    // "Community" placeholder until the store finishes hydrating.
    const apiHit = (apiCommunityData.communityEntities || []).find(
      (e) => String(e.entity_guid).toUpperCase() === normGuid,
    );
    if (apiHit) {
      return {
        entity_guid: apiHit.entity_guid,
        name: apiHit.name,
        entity_type: apiHit.entity_type,
        code: apiHit.code,
        hero_image_url: apiHit.hero_image_url,
        // DEV-890: pass visibility through so the lock icon + private-banner
        // render correctly when this fallback path is the resolver.
        visibility: (apiHit as any).visibility,
        // DEV-968 — info card fields from the seed-shaped API row.
        address: (apiHit as any).address ?? null,
        info_icon_key: (apiHit as any).info_icon_key ?? null,
        info_text: (apiHit as any).info_text ?? null,
        // DEV-980 — tab visibility flags from the seed-shaped API row.
        tab_visibility: (apiHit as any).tab_visibility ?? null,
        // DEV-1013 — primary location for the hero address row.
        primary_location_entity_guid:
          (apiHit as any).primary_location_entity_guid ?? null,
      } as CommunityEntity;
    }
    // DEV-932: removed flatfile fallback. If the entity isn't in the
    // store and the API hasn't returned it yet, the caller renders the
    // "Community" placeholder until hydration completes.
    return undefined;
   
  }, [communityGuid, storeVersion, apiCommunityData.communityEntities]);

  const name = community?.name || "Community";

  /* ---- Country flag resolution for project cards ---- */
  // DEV-932: flatfile-seeded entries removed. Entity + parent maps are
  // built from API data + the in-memory communityConfigStore overrides.
  const entityMap = useMemo(() => {
    const m = new Map<string, CommunityEntity>();
    for (const e of (apiCommunityData.communityEntities || [])) {
      const key = String(e.entity_guid).toUpperCase();
      m.set(key, {
        entity_guid: key,
        name: e.name,
        entity_type: e.entity_type,
        code: e.code,
        hero_image_url: e.hero_image_url,
      } as CommunityEntity);
    }
    // Runtime overrides from communityConfigStore.
    communityConfigStore.forEach((v: any, k: string) => {
      if (!m.has(k)) {
        m.set(k, { entity_guid: k, name: v.name, entity_type: v.entity_type, code: v.code } as CommunityEntity);
      }
    });
    return m;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeVersion, apiCommunityData.communityEntities]);

  const parentMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of (apiCommunityData.communityRelationships || [])) {
      const childKey = String(r.child_entity_guid).toUpperCase();
      m.set(childKey, String(r.parent_entity_guid).toUpperCase());
    }
    return m;
   
  }, [apiCommunityData.communityRelationships]);

  const countryInfo = useMemo(
    () => resolveCountryForEntity(communityGuid, entityMap, parentMap),
    [communityGuid, entityMap, parentMap]
  );

  // DEV-895 / DEV-981 — effective logo via owner-controlled cascade:
  // this community's own logo_url, else the nearest ancestor's. NULL when
  // nobody in the chain has set one, in which case the hero slot falls
  // back to the country flag or first letter.
  const effectiveLogoUrl = useMemo(
    () => resolveEffectiveLogoUrl(
      communityGuid,
      apiCommunityData.communityEntities,
      apiCommunityData.communityRelationships,
    ),
    [communityGuid, apiCommunityData.communityEntities, apiCommunityData.communityRelationships]
  );

  // DEV-895 / DEV-981 v2 — Logo upload pipeline, mirroring UserProfilePage.
  // The hero logo tile is clickable for admins (canOpenSettings). Click →
  // file picker → CircleCropModal → upload to MinIO → persist via
  // updateCommunityEntity. Persists immediately, no Save click required.
  const [logoCrop, setLogoCrop] = useState<{ src: string; w: number; h: number } | null>(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoImgValid, setLogoImgValid] = useState(true);
  const logoFileInputRef = useRef<HTMLInputElement>(null);

  // DEV-895 / DEV-981 v2 — Probe external logo URLs to detect expired CDN
  // links (same defensive pattern as UserProfilePage's profileImgValid).
  // Internal MinIO URLs (/api/, blob:, data:) are trusted without probing.
  useEffect(() => {
    if (!effectiveLogoUrl) { setLogoImgValid(false); return; }
    if (effectiveLogoUrl.startsWith('/api/')
        || effectiveLogoUrl.startsWith('blob:')
        || effectiveLogoUrl.startsWith('data:')) {
      setLogoImgValid(true);
      return;
    }
    const img = new Image();
    img.onload = () => setLogoImgValid(true);
    img.onerror = () => setLogoImgValid(false);
    img.src = effectiveLogoUrl;
  }, [effectiveLogoUrl]);

  const handleLogoFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (file.size > 10 * 1024 * 1024) {
      alert('Image too large (max 10 MB).');
      return;
    }
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file.');
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      setLogoCrop({ src: objectUrl, w: img.naturalWidth, h: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      alert('Could not load that image.');
    };
    img.src = objectUrl;
  }, []);

  const handleLogoCropConfirm = useCallback(async (dataUrl: string) => {
    if (logoCrop) URL.revokeObjectURL(logoCrop.src);
    setLogoCrop(null);
    setLogoUploading(true);
    try {
      const ts = Date.now();
      const result = await uploadDataUrl(dataUrl, 'community-logos', communityGuid, `community-logo-${ts}.png`);
      const url = `${fileUrl(result.key)}?v=${ts}`;
      await updateCommunityEntity(communityGuid, { logoUrl: url });
    } catch (err: any) {
      console.warn('[CommunityDetailPage] Logo upload failed:', err?.message || err);
      try { await updateCommunityEntity(communityGuid, { logoUrl: dataUrl }); } catch { /* ignore */ }
    } finally {
      setLogoUploading(false);
    }
  }, [logoCrop, communityGuid]);

  const handleLogoCropCancel = useCallback(() => {
    if (logoCrop) URL.revokeObjectURL(logoCrop.src);
    setLogoCrop(null);
  }, [logoCrop]);

  // DEV-932: security data sourced from the API hook. The localStorage
  // override layer (getCommunityRoleOverrides) is removed — the DB is the
  // source of truth for community_role_assignments.
  const { securityUsers, communityRoles, communityMemberships, refetch: refetchSecurity } = useApiSecurityData();

  // ── DEV-1414 — member-side "Request to join" (org communities only;
  // geo self-join is a separate door). State machine over the viewer's
  // membership rows for THIS community: none → offer; pending → waiting;
  // invited/active/suspended/banned → no affordance (invited resolves in
  // the Action Center; the rest aren't request states).
  const [joinOpen, setJoinOpen] = useState(false);
  const [joinMessage, setJoinMessage] = useState('');
  const [joinBusy, setJoinBusy] = useState(false);

  const isOrgCommunity = useMemo(() => {
    const et = String(community?.entity_type || '').toLowerCase();
    return et === 'organization' || et.startsWith('org_');
  }, [community]);

  const myMembershipStatus = useMemo<string | null>(() => {
    if (!currentUserGuid) return null;
    const cu = String(currentUserGuid).toUpperCase();
    const cg = String(communityGuid).toUpperCase();
    let status: string | null = null;
    for (const m of communityMemberships || []) {
      if (
        String((m as any).user_guid || '').toUpperCase() === cu &&
        String((m as any).community_entity_guid || '').toUpperCase() === cg
      ) {
        const s = String((m as any).status || 'active').toLowerCase();
        if (s === 'active') return 'active'; // strongest state wins
        if (s !== 'inactive') status = s;
      }
    }
    return status;
  }, [currentUserGuid, communityGuid, communityMemberships]);

  const showJoinButton =
    isOrgCommunity && !!currentUserGuid && myMembershipStatus === null;
  const showJoinPending = isOrgCommunity && myMembershipStatus === 'pending';

  // ── DEV-1414 chunk 2 — geo self-join (§8: geo = self-declared, no
  // approval). Join writes an active 'joined' row immediately; leavable
  // per §8.4 ruling 3. Only on ACTIVE geo communities (inactive geos have
  // no reachable page anyway per DEV-890, belt-and-braces here).
  const isGeoCommunity = useMemo(
    () => String(community?.entity_type || '').toLowerCase().startsWith('geo_'),
    [community],
  );
  const showGeoJoin =
    isGeoCommunity &&
    !!currentUserGuid &&
    myMembershipStatus === null &&
    (community as any)?.is_active !== false;

  // Leave is offered ONLY when 'joined' is the user's sole active basis
  // here. Declared home/operating rows are managed in Account Settings —
  // "leaving" a declaration from a community page would silently
  // contradict the profile; and if a declaration coexists with a joined
  // row, removing the joined row is a member-visible no-op.
  const myLeavableJoinedRowId = useMemo<string | null>(() => {
    if (!isGeoCommunity || !currentUserGuid) return null;
    const cu = String(currentUserGuid).toUpperCase();
    const cg = String(communityGuid).toUpperCase();
    let joinedId: string | null = null;
    for (const m of communityMemberships || []) {
      if (
        String((m as any).user_guid || '').toUpperCase() !== cu ||
        String((m as any).community_entity_guid || '').toUpperCase() !== cg ||
        String((m as any).status || 'active').toLowerCase() !== 'active'
      ) continue;
      const type = String((m as any).membership_type || '').toLowerCase();
      if (type === 'joined') {
        joinedId = (m as any).id ? String((m as any).id) : null;
      } else {
        return null; // another active basis exists — no Leave affordance
      }
    }
    return joinedId;
  }, [isGeoCommunity, currentUserGuid, communityGuid, communityMemberships]);

  const geoJoin = useCallback(async () => {
    if (joinBusy || !currentUserGuid) return;
    setJoinBusy(true);
    try {
      const { upsertCommunityMembership } = await import('../../api/security');
      await upsertCommunityMembership({
        userGuid: String(currentUserGuid),
        communityEntityGuid: communityGuid,
        membershipType: 'joined',
        status: 'active',
      });
      refetchSecurity();
    } catch (err: any) {
      window.alert(err?.message || 'Join failed.');
    } finally {
      setJoinBusy(false);
    }
  }, [joinBusy, currentUserGuid, communityGuid, refetchSecurity]);

  const geoLeave = useCallback(async () => {
    if (joinBusy || !myLeavableJoinedRowId) return;
    if (!window.confirm(t('communityDetail.confirmLeave', { name: String(community?.name || '') }))) return;
    setJoinBusy(true);
    try {
      const { deactivateCommunityMembership } = await import('../../api/security');
      await deactivateCommunityMembership(myLeavableJoinedRowId);
      refetchSecurity();
    } catch (err: any) {
      window.alert(err?.message || 'Leave failed.');
    } finally {
      setJoinBusy(false);
    }
  }, [joinBusy, myLeavableJoinedRowId, community, t, refetchSecurity]);

  const submitJoinRequest = useCallback(async () => {
    if (joinBusy || !currentUserGuid) return;
    setJoinBusy(true);
    try {
      const { submitMembershipRequest, resolveCommunityAdminGuids } = await import(
        '../../Data/Membership/membershipAdmission'
      );
      const cu = String(currentUserGuid).toUpperCase();
      const myName =
        (securityUsers || []).find(
          (u: any) => String(u.user_guid || '').toUpperCase() === cu,
        )?.display_name || '';
      await submitMembershipRequest({
        currentUserGuid: String(currentUserGuid),
        currentUserName: String(myName),
        communityGuid,
        communityName: String(community?.name || ''),
        message: joinMessage,
        adminGuids: resolveCommunityAdminGuids(communityRoles, communityGuid),
      });
      setJoinOpen(false);
      setJoinMessage('');
      refetchSecurity();
    } catch (err: any) {
      window.alert(err?.message || 'Request failed.');
    } finally {
      setJoinBusy(false);
    }
  }, [joinBusy, currentUserGuid, securityUsers, communityGuid, community, joinMessage, communityRoles, refetchSecurity]);

  // DEV-980: elevated viewers (owner, admin, content_creator, or sysadmin)
  // always see every tab regardless of tab_visibility flags. Other viewers
  // see only tabs that are enabled in the JSON column (or always-visible
  // tabs like Alignment / Feed).
  const isElevatedViewer = useMemo(() => {
    if (!currentUserGuid) return false;
    const cu = String(currentUserGuid).toUpperCase();
    const cg = String(communityGuid).toUpperCase();
    const u = (securityUsers || []).find((x: any) => String(x.user_guid).toUpperCase() === cu);
    if (u?.is_system_admin) return true;
    const role = (communityRoles || []).find(
      (r: any) => String(r.user_guid).toUpperCase() === cu
                  && String(r.community_entity_guid).toUpperCase() === cg
                  && (r.is_active ?? true) !== false
    )?.role;
    return role === "owner" || role === "admin" || role === "content_creator";
  }, [communityGuid, currentUserGuid, securityUsers, communityRoles]);

  // DEV-1190: is the current viewer a member of this community? Used to
  // gate the Management tab when management_visibility='community'. A
  // member is anyone with an active row in community_memberships OR an
  // active community_role_assignment (community admins/owners both have
  // role rows but may not have a membership row, depending on how they
  // were granted access; treat either as "member" for read-access).
  const isCommunityMember = useMemo(() => {
    if (!currentUserGuid) return false;
    const cu = String(currentUserGuid).toUpperCase();
    const cg = String(communityGuid).toUpperCase();
    // DEV-1414: status === 'active' is the membership gate. The old
    // (m.is_active ?? true) check tested a field membership rows don't
    // have, so ANY row counted — harmless while the API returned only
    // active rows, wrong now that pending/invited/suspended rows flow to
    // the client (a pending requester must not pass the member gate).
    const hasMembership = (communityMemberships || []).some(
      (m: any) =>
        String(m.user_guid).toUpperCase() === cu
        && String(m.community_entity_guid).toUpperCase() === cg
        && String(m.status || 'active').toLowerCase() === 'active'
    );
    if (hasMembership) return true;
    const hasRole = (communityRoles || []).some(
      (r: any) =>
        String(r.user_guid).toUpperCase() === cu
        && String(r.community_entity_guid).toUpperCase() === cg
        && (r.is_active ?? true) !== false
    );
    return hasRole;
  }, [currentUserGuid, communityGuid, communityMemberships, communityRoles]);

  // Skip ruling 2026-07-07 (chunk-A live test): a plain member with NO active
  // role must not see the Management tab. This wires the DEV-1190 'team'-tier
  // role cross-check that Phase 1 deferred: an active community_role_assignment
  // on THIS community (any role — Secretary, investigator, …) passes; a bare
  // membership does not. Elevated viewers pass upstream in visibleTabs.
  const hasActiveCommunityRole = useMemo(() => {
    if (!currentUserGuid) return false;
    const cu = String(currentUserGuid).toUpperCase();
    const cg = String(communityGuid).toUpperCase();
    return (communityRoles || []).some(
      (r: any) =>
        String(r.user_guid).toUpperCase() === cu
        && String(r.community_entity_guid).toUpperCase() === cg
        && (r.is_active ?? true) !== false,
    );
  }, [currentUserGuid, communityGuid, communityRoles]);

  // ── Voice-ladder posting gate (Skip 2026-07-07; §7.5 as amended) ──
  // Reading is entry; POSTING is voice. Contributor+ (and legacy accounts
  // with no onboarding profile) post as members. An Explorer posts ONLY on
  // an org community where they are a member, after acknowledging the
  // platform community standards (+ the org's designated guidelines when
  // present). Client-side mirror of the NewsController.Share server gate —
  // the server remains the authority.
  const { archetype: viewerArchetype } = useApiOnboarding(currentUserGuid || null);
  const { guidelines: orgGuidelinesState } = useApiCommunityGuidelines(communityGuid);
  const canPostFeed = useMemo(() => {
    if (!isCommunityMember) return false;
    if (viewerArchetype !== 'explorer') return true; // contributor+ / legacy-permissive
    const et = String(community?.entity_type || '').toLowerCase();
    const isOrg = et === 'organization' || et.startsWith('org_');
    if (!isOrg) return false; // geo feeds stay Contributor+
    const cu = currentUserGuid ? String(currentUserGuid).toUpperCase() : '';
    const viewer = (securityUsers || []).find(
      (x: any) => String(x.user_guid).toUpperCase() === cu,
    );
    const standardsCurrent =
      !!viewer?.community_standards_accepted_at &&
      (viewer?.community_standards_version_accepted ?? 0) >= STANDARDS_CURRENT_VERSION;
    if (!standardsCurrent) return false;
    const guidelinesOk =
      !orgGuidelinesState?.content || orgGuidelinesState.acknowledged;
    return guidelinesOk;
  }, [isCommunityMember, viewerArchetype, community, currentUserGuid, securityUsers, orgGuidelinesState]);

  // DEV-980 + DEV-1190: derived list of visible tabs for the current viewer.
  // Alignment and Feed are always visible; News / Health / Forum / Media
  // respect the tab_visibility JSON when the viewer is not elevated.
  // Management respects community_entities.management_visibility (public /
  // community / team). Elevated viewers always see all tabs.
  const visibleTabs = useMemo<TabKey[]>(() => {
    if (isElevatedViewer) return VALID_TABS;
    const flags = parseTabVisibility(community?.tab_visibility);
    // DEFAULT flipped 'community' → 'team' (Skip, 2026-07-07): out of the box,
    // Management is for role-holders and admins — plain members (e.g. a
    // day-zero invited Explorer) don't see ops surfaces. Communities that
    // want member-visible (or public) Management opt in via settings; the
    // explicit tiers keep their DEV-1190 meanings.
    const mgmt = String((community as any)?.management_visibility || 'team').toLowerCase();
    return VALID_TABS.filter((key) => {
      if (key === "alignment" || key === "feed" || key === "playbooks") return true;
      if (key === "management") {
        // DEV-1190 visibility ladder, 'team' cross-check now wired: active
        // role-holders on this community (+ elevated viewers, upstream).
        if (mgmt === 'public') return true;
        if (mgmt === 'community') return isCommunityMember;
        if (mgmt === 'team') return hasActiveCommunityRole;
        return false;
      }
      const v = (flags as any)[key];
      // Undefined → legacy default visible. Explicit false → hide.
      return v !== false;
    });
  }, [isElevatedViewer, community, isCommunityMember, hasActiveCommunityRole]);

  // DEV-980: if the current `tab` becomes hidden (admin toggled it off while
  // viewer is on it, or viewer landed via deep-link to a hidden tab), bounce
  // them to Alignment.
  useEffect(() => {
    if (!visibleTabs.includes(tab)) {
      setTabState(visibleTabs[0] || "alignment");
    }
  }, [visibleTabs, tab]);

  const canOpenSettings = useMemo(() => {
    if (!currentUserGuid) return false;
    // DEV-932 Phase 7 follow-up: case-insensitive GUID compares per CLAUDE.md.
    // API returns lowercase, auth context uppercase; without normalisation
    // sysadmin lookup misses and the user loses access.
    //
    // BUGFIX: securityUsers / communityRoles MUST be in the deps array.
    // useApiSecurityData fetches asynchronously, so on first render both
    // arrays are empty and this memo resolves to `false`. Without these
    // deps, useMemo never recomputes when the API data arrives, leaving
    // sysadmins (and community admins/owners) permanently locked out
    // until the next unrelated re-render that happens to invalidate
    // [communityGuid, currentUserGuid].
    const cu = String(currentUserGuid || "").toUpperCase();
    const cg = String(communityGuid || "").toUpperCase();
    const u = (securityUsers || []).find((x: any) => String(x.user_guid).toUpperCase() === cu);
    if (u?.is_system_admin) return true;
    const role = (communityRoles || []).find(
      (r: any) => String(r.user_guid).toUpperCase() === cu
                  && String(r.community_entity_guid).toUpperCase() === cg
                  && (r.is_active ?? true) !== false
    )?.role;
    return role === "admin" || role === "owner";
  }, [communityGuid, currentUserGuid, securityUsers, communityRoles]);

  /* ---- Community hero image ---- */
  const resolveCommunityHeroUrl = useCallback((): string | null => {
    if (!communityGuid) return null;
    try {
      const ov = getCommunityOverride(communityGuid) as any;
      if (ov?.hero_image_url) return mediaSrc(ov.hero_image_url as string);
    } catch { /* ignore */ }
    return mediaSrc(community?.hero_image_url || null);
  }, [communityGuid, community]);

  const [communityHeroUrl, setCommunityHeroUrl] = useState<string | null>(resolveCommunityHeroUrl);

  // DEV-884: Always sync to the resolved value — including null. The previous
  // `if (resolved) setCommunityHeroUrl(resolved)` guard caused a state leak:
  // when navigating from a community with a custom cover (Boston) to one
  // without (Hampton Rotary Club), `resolved` came back null and the state
  // retained the previous community's hero URL — making it look like the new
  // community had inherited Boston's cover photo.
  useEffect(() => {
    setCommunityHeroUrl(resolveCommunityHeroUrl());
  }, [resolveCommunityHeroUrl]);

  const [communityShowUrlInput, setCommunityShowUrlInput] = useState(false);
  const [communityUrlDraft, setCommunityUrlDraft] = useState('');
  const [communityCropState, setCommunityCropState] = useState<CommunityCropState | null>(null);
  const communityFileInputRef = useRef<HTMLInputElement>(null);
  const [heroMenuOpen, setHeroMenuOpen] = useState(false);
  const heroMenuRef = useRef<HTMLDivElement>(null);

  const persistCommunityHeroUrl = useCallback((url: string) => {
    if (!communityGuid) return;
    setCommunityOverride(communityGuid, { hero_image_url: url });
    setCommunityHeroUrl(url);
  }, [communityGuid]);

  const handleCommunityHeroFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('Please select an image file.'); return; }
    if (file.size > COMMUNITY_MAX_RAW_FILE_BYTES) { alert(`Image is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum is 10 MB.`); return; }
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { setCommunityCropState({ src: objectUrl, naturalW: img.naturalWidth, naturalH: img.naturalHeight }); };
    img.onerror = () => { URL.revokeObjectURL(objectUrl); alert('Failed to load image.'); };
    img.src = objectUrl;
    e.target.value = '';
  }, []);

  const handleCommunityCropConfirm = useCallback((dataUrl: string) => {
    if (communityCropState) URL.revokeObjectURL(communityCropState.src);
    setCommunityCropState(null);
    if (communityGuid) {
      const ts = Date.now();
      uploadDataUrl(dataUrl, 'community-heroes', communityGuid, `community-hero-${ts}.jpg`)
        .then((result) => {
          const storedUrl = `${fileUrl(result.key)}?v=${ts}`;
          console.log('[CommunityHero] Uploaded to MinIO:', storedUrl);
          persistCommunityHeroUrl(storedUrl);
        })
        .catch((err) => {
          console.warn('[CommunityHero] MinIO upload failed, falling back to data URL:', err);
          persistCommunityHeroUrl(dataUrl);
        });
    } else {
      persistCommunityHeroUrl(dataUrl);
    }
  }, [communityCropState, persistCommunityHeroUrl, communityGuid]);

  const handleCommunityCropCancel = useCallback(() => {
    if (communityCropState) URL.revokeObjectURL(communityCropState.src);
    setCommunityCropState(null);
  }, [communityCropState]);

  /** Fetch as blob to avoid canvas taint from cross-origin image URLs. */
  const handleCommunityEditHero = useCallback(() => {
    if (!communityHeroUrl) return;
    fetch(communityHeroUrl)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => { setCommunityCropState({ src: blobUrl, naturalW: img.naturalWidth, naturalH: img.naturalHeight }); };
        img.onerror = () => { URL.revokeObjectURL(blobUrl); alert('Could not load the current hero image for editing.'); };
        img.src = blobUrl;
      })
      .catch(() => { alert('Could not load the current hero image for editing.'); });
  }, [communityHeroUrl]);

  const handleCommunityUrlSubmit = useCallback(() => {
    const url = communityUrlDraft.trim();
    if (!url) return;
    if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('data:')) {
      alert('Please enter a valid URL starting with http:// or https://'); return;
    }
    if (url.startsWith('data:') && communityGuid) {
      uploadDataUrl(url, 'community-heroes', communityGuid, 'community-hero.jpg')
        .then((result) => { persistCommunityHeroUrl(fileUrl(result.key)); })
        .catch(() => { persistCommunityHeroUrl(url); });
    } else {
      persistCommunityHeroUrl(url);
    }
    setCommunityShowUrlInput(false);
    setCommunityUrlDraft('');
  }, [communityUrlDraft, persistCommunityHeroUrl, communityGuid]);

  const handleRemoveCommunityHero = useCallback(() => {
    if (!communityGuid) return;
    setCommunityOverride(communityGuid, { hero_image_url: '' });
    setCommunityHeroUrl(null);
  }, [communityGuid]);

  // Close hero menu on click outside
  useEffect(() => {
    if (!heroMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (heroMenuRef.current && !heroMenuRef.current.contains(e.target as Node)) {
        setHeroMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [heroMenuOpen]);

  const openSettings = () => {
    try { window?.localStorage?.setItem(LS_SELECTED_COMMUNITY_KEY, String(communityGuid)); } catch {}
    setSettingsOpen(true);
  };

  /* ---- Hero dropdown menu styles ---- */
  const heroMenuItemStyle: React.CSSProperties = {
    display: 'block', width: '100%', textAlign: 'start',
    background: 'transparent', border: 'none',
    borderBottom: '1px solid var(--border-subtle)',
    color: 'var(--text-tertiary)', fontSize: 13, fontWeight: 500,
    padding: '10px 16px', cursor: 'pointer',
    transition: 'background 0.15s, color 0.15s',
  };

  /* Subtle gradient overlay for hero text readability — bottom edge only */
  const overlayStyle: React.CSSProperties = {
    background: communityHeroUrl
      ? 'linear-gradient(to top, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.12) 35%, transparent 65%)'
      : 'linear-gradient(to top, rgba(0,0,0,0.30) 0%, transparent 50%)',
  };

  return (
    <div className={styles.page} ref={containerRef} style={{ position: 'relative' }}>
      {/* DEV-1452: in-app back button removed. LeftNav + breadcrumb carry the
          path out; browser back covers history. */}

      {/* Unified hero header — matches PillarPageHeader pattern */}
      <div className={styles.headerOuter} style={heroMenuOpen ? { overflow: 'visible' } : undefined}>
        {/* Blurred bleed background */}
        {communityHeroUrl && (
          <div className={styles.heroBleed} style={{ backgroundImage: `url(${communityHeroUrl})` }} />
        )}

        <div className={styles.header} style={heroMenuOpen ? { overflow: 'visible', zIndex: 20 } : undefined}>
          {/* Hero background or placeholder */}
          {communityHeroUrl ? (
            <div className={styles.heroBg} style={{ backgroundImage: `url(${communityHeroUrl})` }} />
          ) : (
            <div className={styles.heroPlaceholder} />
          )}
          <div className={styles.heroOverlay} style={overlayStyle} />

          {/* Crop overlay */}
          {communityCropState && (
            <CommunityCropOverlay crop={communityCropState} onConfirm={handleCommunityCropConfirm} onCancel={handleCommunityCropCancel} />
          )}

          {/* DEV-890 — Large lock overlay in upper-left of hero when community is private.
              Visible to ALL viewers as a primary visibility marker. Native title attribute
              provides hover tooltip. For users who can change visibility (canOpenSettings),
              the lock is rendered as a button that opens Community Settings on click.
              For everyone else it's a non-interactive indicator. */}
          {(community as any)?.visibility === 'private' && !communityCropState && (() => {
            const lockTitle = canOpenSettings
              ? 'Private community — click to open settings and change visibility. Only members, community admins, and org admins in the ancestor chain can see this page. Public visitors do not have access.'
              : 'Private community — only members, community admins, and org admins in the ancestor chain can see this page. Public visitors do not have access.';
            const baseStyle: React.CSSProperties = {
              position: 'absolute',
              top: 16,
              insetInlineStart: 16,
              width: 56,
              height: 56,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 28,
              borderRadius: 14,
              background: 'rgba(0, 0, 0, 0.55)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              color: '#fff',
              zIndex: 10,
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.35)',
            };
            if (canOpenSettings) {
              return (
                <button
                  type="button"
                  aria-label="Private community — open settings to change visibility"
                  title={lockTitle}
                  onClick={openSettings}
                  style={{ ...baseStyle, cursor: 'pointer', padding: 0, font: 'inherit' }}
                >
                  🔒
                </button>
              );
            }
            return (
              <div
                role="img"
                aria-label="Private community"
                title={lockTitle}
                style={{ ...baseStyle, cursor: 'help' }}
              >
                🔒
              </div>
            );
          })()}

          {/* ── Top-right zone: Follow (all viewers) + admin controls ── */}
          {!communityCropState && (
            <div className={styles.communityTopRight} ref={heroMenuRef}>
              {/* DEV-1332 / DEV-1387 — Follow sits inline with the settings gear
                  in a single top-right row; the cover-photo control flows to a
                  second row. Follow renders for every viewer; the gear + cover
                  controls stay gated behind canOpenSettings. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {/* DEV-1414 — org membership request door. Follow ≠ membership:
                    follow is the social edge, this requests belonging. */}
                {showJoinButton && (
                  <button
                    type="button"
                    onClick={() => setJoinOpen(true)}
                    style={{
                      background: 'var(--accent-primary)', color: '#fff',
                      border: 'none', borderRadius: 8, padding: '7px 14px',
                      fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                    }}
                  >
                    {t('communityDetail.requestToJoin')}
                  </button>
                )}
                {showJoinPending && (
                  <span
                    style={{
                      color: 'var(--color-warning)', border: '1px solid var(--color-warning)',
                      borderRadius: 999, padding: '4px 12px', fontSize: 12, fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t('communityDetail.requestPending')}
                  </span>
                )}
                {/* DEV-1414 chunk 2 — geo self-join: instant, no approval. */}
                {showGeoJoin && (
                  <button
                    type="button"
                    onClick={geoJoin}
                    disabled={joinBusy}
                    style={{
                      background: 'var(--accent-primary)', color: '#fff',
                      border: 'none', borderRadius: 8, padding: '7px 14px',
                      fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                      opacity: joinBusy ? 0.6 : 1,
                    }}
                  >
                    {t('communityDetail.join')}
                  </button>
                )}
                {/* Leave — only when 'joined' is the sole active basis. */}
                {myLeavableJoinedRowId && (
                  <button
                    type="button"
                    onClick={geoLeave}
                    disabled={joinBusy}
                    style={{
                      background: 'transparent', color: 'var(--text-muted)',
                      border: '1px solid var(--border-default)', borderRadius: 8,
                      padding: '7px 12px', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap',
                    }}
                  >
                    {t('communityDetail.leaveCommunity')}
                  </button>
                )}
                {/* DEV-1415 follow-up (Skip, 2026-07-02): members don't see
                    Follow — FeedController already unions member communities
                    with followed ones, so for a member the follow edge is a
                    functional no-op and the un-toggled button misleads.
                    Follow stays for NON-members (the spectator edge — its
                    actual purpose). Data lifecycles remain unmerged per the
                    slice-1 fence; this is display-only. */}
                {myMembershipStatus !== 'active' && (
                  <FollowButton targetType="community" targetGuid={communityGuid} compact showCount={false} />
                )}
                {canOpenSettings && (
              <div className={styles.actions}>
                <button className={styles.actionBtn} type="button" onClick={openSettings} title={t('communityDetail.settings')} aria-label="Settings">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </button>
              </div>
                )}
              </div>

              {/* Edit cover photo — flows below the Follow + settings row */}
              {canOpenSettings && (
              <div className={styles.heroEditControlsInline}>
                <input ref={communityFileInputRef} type="file" accept="image/*" onChange={handleCommunityHeroFileChange} style={{ display: 'none' }} />
                <button type="button" onClick={() => setHeroMenuOpen((v) => !v)} style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  background: '#fff', color: '#1e293b',
                  border: 'none', borderRadius: 8, padding: '7px 14px',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  transition: 'background 0.15s',
                  whiteSpace: 'nowrap',
                }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--text-primary)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#fff'; }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                  {t('communityDetail.editCoverPhoto')}
                </button>

                {heroMenuOpen && (
                  <div style={{
                    position: 'absolute', top: '100%', insetInlineEnd: 0,
                    marginTop: 6,
                    background: 'var(--bg-elevated)', backdropFilter: 'blur(8px)',
                    borderRadius: 10, overflow: 'hidden',
                    border: '1px solid var(--border-default)',
                    boxShadow: '0 8px 28px rgba(0,0,0,0.5)', minWidth: 180,
                    zIndex: 60,
                  }}>
                    <button type="button" onClick={() => { communityFileInputRef.current?.click(); setHeroMenuOpen(false); }} style={heroMenuItemStyle}>
                      {communityHeroUrl ? 'Change Photo' : 'Upload Photo'}
                    </button>
                    {communityHeroUrl && (
                      <button type="button" onClick={() => { handleCommunityEditHero(); setHeroMenuOpen(false); }} style={heroMenuItemStyle}>
                        Edit Crop
                      </button>
                    )}
                    <button type="button" onClick={() => {
                      setCommunityShowUrlInput((v) => !v); setCommunityUrlDraft(''); setHeroMenuOpen(false);
                    }} style={heroMenuItemStyle}>
                      Paste URL
                    </button>
                    {communityHeroUrl && (
                      <button type="button" onClick={() => { handleRemoveCommunityHero(); setHeroMenuOpen(false); }} style={{
                        ...heroMenuItemStyle, color: '#f87171', borderBottom: 'none',
                      }}>
                        Remove Photo
                      </button>
                    )}
                  </div>
                )}

                {communityShowUrlInput && !heroMenuOpen && (
                  <div style={{
                    position: 'absolute', top: '100%', insetInlineEnd: 0,
                    marginTop: 6, display: 'flex', gap: 6, maxWidth: 420,
                  }}>
                    <input type="text" value={communityUrlDraft} onChange={(e) => setCommunityUrlDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleCommunityUrlSubmit();
                        if (e.key === 'Escape') { setCommunityShowUrlInput(false); setCommunityUrlDraft(''); }
                      }}
                      placeholder="https://example.com/image.jpg"
                      autoFocus
                      style={{
                        flex: 1, background: 'rgba(0,0,0,0.70)',
                        border: '1px solid var(--border-subtle)', borderRadius: 6,
                        color: 'var(--text-secondary)', fontSize: 12, padding: '5px 10px', outline: 'none', minWidth: 180,
                      }} />
                    <button type="button" onClick={handleCommunityUrlSubmit} style={{
                      background: 'rgba(69,187,71,0.70)', border: '1px solid var(--border-default)',
                      borderRadius: 6, color: 'var(--text-primary)', fontSize: 12, fontWeight: 600,
                      padding: '5px 12px', cursor: 'pointer',
                    }}>Save</button>
                  </div>
                )}
              </div>
              )}
            </div>
          )}

          {/* DEV-1456 — Identity row: 104px avatar anchored to the hero's
              bottom edge at inline-start, name block beside it. This is the
              user-profile grammar exactly: the avatar sits INSIDE the hero,
              bottom-anchored, so the photo stays flush against the tab strip.
              (First cut straddled the hero's edge on the page background —
              Skip rejected it 2026-07-12: it drove a dead band between the
              photo and the menu and disconnected the two.) */}
          <div className={styles.identityRow}>
              {/* DEV-895 / DEV-981 v2 — Effective community logo via the
                  inheritance cascade. Admins click to upload + crop (same
                  flow as the user-profile avatar). Falls back to country
                  flag, then first letter, when no ancestor has set a logo.
                  Image-validity probe mirrors profileImgValid in
                  UserProfilePage so expired external URLs degrade
                  gracefully. */}
              <div
                className={styles.logo}
                onClick={canOpenSettings && !logoUploading ? () => logoFileInputRef.current?.click() : undefined}
                title={canOpenSettings ? 'Click to change logo' : undefined}
                style={{
                  cursor: canOpenSettings && !logoUploading ? 'pointer' : 'default',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {effectiveLogoUrl && logoImgValid ? (
                  <img
                    src={effectiveLogoUrl}
                    alt=""
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: 'inherit',
                    }}
                  />
                ) : countryInfo.flag ? (
                  <span className={styles.flagEmoji}>{countryInfo.flag}</span>
                ) : (
                  name.charAt(0).toUpperCase()
                )}
                {canOpenSettings && !logoUploading && (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'rgba(0,0,0,0.45)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: 0,
                      transition: 'opacity 0.15s',
                      borderRadius: 'inherit',
                    }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.opacity = '0'; }}
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                      <circle cx="12" cy="13" r="4" />
                    </svg>
                  </div>
                )}
                {logoUploading && (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'rgba(0,0,0,0.6)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: 11,
                      fontWeight: 600,
                      borderRadius: 'inherit',
                    }}
                  >
                    …
                  </div>
                )}
              </div>
              <input
                ref={logoFileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleLogoFileChange}
              />

              <div className={styles.titleBlock}>
                <div className={styles.title}>{name}</div>
                <CommunityBreadcrumb communityGuid={communityGuid} communityName={name} onNavigate={(guid) => navigate(`/communities/${guid}`)} />
                {/* DEV-1013 — primary location row below the subcommunity path.
                    DEV-1456 follow-up (Skip, 2026-07-12): suppressed for geo_*
                    communities. On a geo entity the "primary location" IS the
                    entity itself, so it restated the breadcrumb — Boston showed
                    "Massachusetts, United States" and then "Boston, Massachusetts,
                    United States". The row still earns its place on org_*
                    communities, whose location is NOT their ancestry (a Rotary
                    club's parent is a district, not a place). */}
                {!isGeoCommunity && (
                  <CommunityPrimaryLocation
                    primaryLocationGuid={community?.primary_location_entity_guid}
                    onNavigate={(guid) => navigate(`/communities/${guid}`)}
                  />
                )}
                {/* DEV-1332 — Follow relocated to the hero top-right zone
                    (see .communityTopRight above), matching the projects
                    pillar hero. */}
              </div>
          </div>
        </div>
      </div>

      {/* DEV-890 — banner removed in favor of the large lock overlay on the hero
          (with hover tooltip). The lock badge is sufficient signal; the banner
          was visual noise. Admins see the lock the same as members; the path to
          flip visibility runs through Settings → Audience & Permissions, which
          admins know how to find. */}

      {/* Tab bar — sticky, docks below title bar when hero scrolls away.
          DEV-969: order is Alignment, News, Feed, Health, Forum, Media.
          DEV-980: non-elevated viewers see only tabs enabled in
          tab_visibility. Elevated viewers (owner/admin/content_creator/
          sysadmin) always see all tabs. */}
      <div className={styles.tabRow}>
        {visibleTabs.map((tabKey) => {
          const labels: Record<TabKey, string> = {
            alignment: t('communityDetail.alignment'),
            playbooks: t('communityDetail.playbooksTab'),
            news: t('communityDetail.newsTab'),
            feed: t('communityDetail.feed'),
            health: t('communityDetail.healthTab'),
            forum: t('communityDetail.forum'),
            media: t('communityDetail.mediaTab'),
            // DEV-1190
            management: t('communityDetail.managementTab'),
          };
          return (
            <button key={tabKey} type="button" className={tab === tabKey ? styles.tabActive : styles.tab} onClick={() => setTab(tabKey)}>
              {labels[tabKey]}
            </button>
          );
        })}
      </div>

      <div className={styles.body}>
        {tab === "playbooks" && (
          <CommunityPlaybooksTab
            communityGuid={communityGuid}
            currentUserGuid={currentUserGuid ?? ''}
            canAuthor={isElevatedViewer}
          />
        )}
        {tab === "health" && (
          <div style={{ padding: '0 24px' }}>
            <CommunityHealthTab
              communityGuid={communityGuid}
              currentUserGuid={currentUserGuid || ''}
            />
          </div>
        )}
        {tab === "alignment" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
            {/* Onboarding chunk A (§7.9) — one-time welcome nudge for the
                org-invited Explorer landing here. Renders null for everyone
                else; dismissal persists server-side on the profile. */}
            <WelcomeNudge
              communityGuid={communityGuid}
              communityName={name}
              currentUserGuid={currentUserGuid}
            />
            <CommunityInfoCard
              address={community?.address}
              infoIconKey={community?.info_icon_key}
              infoText={community?.info_text}
              canEdit={canOpenSettings}
              onEdit={openSettings}
            />
            <CommunityMvvSection
              communityGuid={communityGuid}
              canEdit={canOpenSettings}
            />
            <CommunityEnablers
              communityGuid={communityGuid}
              canEdit={canOpenSettings}
            />
            <ProjectRow
              communityGuid={communityGuid}
              maxVisible={3}
              sectionTitle={t("communityDetail.priorityProjects")}
              // DEV-970 — always advertise the all-projects landing, even
              // when ≤3 priority projects exist. Keeps the Alignment tab
              // compact (no wall of cards) while making the full list
              // one click away.
              alwaysShowViewAll
              onOpen={(guid) =>
                navigate(`/projects/${guid}`, {
                  state: {
                    // DEV-1452: returnTo no longer feeds a back button (removed).
                    // It still feeds ProjectDetailPage's close/restore handlers.
                    returnTo: `/communities/${communityGuid}#alignment`,
                    viewMode: "member",
                  },
                })
              }
              onViewAll={() => navigate(`/communities/${communityGuid}/projects`)}
              countryFlag={countryInfo.flag}
              countryName={countryInfo.name}
            />
            <WorkspaceList
              communityGuid={communityGuid}
              maxVisible={3}
              sectionTitle={t("communityDetail.priorityProblems")}
              onOpen={(wsGuid) => navigate(`/communities/${communityGuid}/workspaces/${wsGuid}`)}
              onCreate={() => setCreateWorkspaceOpen(true)}
              refreshKey={workspaceRefreshKey}
              onViewAll={() => navigate(`/communities/${communityGuid}/workspaces`)}
              communityName={name}
              communityFlag={countryInfo.flag}
            />
            {/* §7.9 — the durable home of the org's designated guidelines
                (the Getting-started "Read this" row points here). Collapsed
                reference panel at the BOTTOM of About (Skip 2026-07-07 — it's
                reference material, not hero content); renders nothing when
                the org has none. */}
            <CommunityGuidelinesSection communityGuid={communityGuid} />
          </div>
        )}
        {tab === "news" && (
          <CommunityNewsFeed
            communityGuid={communityGuid}
            communityName={name}
            canEdit={canOpenSettings}
            currentUserGuid={currentUserGuid}
          />
        )}
        {tab === "forum" && (
          <div className={styles.panel}>
            <div className={styles.panelTitle}>{t('communityDetail.forum')}</div>
            <div className={styles.placeholder}>{t('communityDetail.placeholderShort')}</div>
          </div>
        )}
        {tab === "feed" && (
          <CommunityFeed
            communityGuid={communityGuid}
            communityName={name}
            users={securityUsers}
            currentUserGuid={currentUserGuid}
            isSystemAdmin={
              !!(currentUserGuid && (securityUsers || []).find(
                (x: any) => String(x.user_guid).toUpperCase() === String(currentUserGuid).toUpperCase() && x.is_system_admin
              ))
            }
            canPost={canPostFeed}
            postingLockedHint={
              // Explorer member who hasn't earned voice here yet — show the
              // earning path instead of silently hiding the composer.
              isCommunityMember && !canPostFeed && viewerArchetype === 'explorer'
                ? t('communityDetail.feedPostingLocked')
                : undefined
            }
            onNavigateToTab={(t) => setTab(t as TabKey)}
          />
        )}
        {tab === "media" && (
          <div style={{ padding: '0 24px' }}>
            {/* DEV-969 — Community media gallery. Reuses ProgressGallery
                via entityType='community'. Admins (canOpenSettings) get
                upload UI; non-admins browse and lightbox-view. */}
            <ProgressGallery
              entityType="community"
              entityGuid={communityGuid}
              canUpload={canOpenSettings}
              titleOverride={t('communityDetail.mediaTab')}
            />
          </div>
        )}
        {tab === "management" && (
          // DEV-1190 — Community Management tab (peer to News/Feed/etc).
          // Sub-tabs: Team & Roles, Priorities, Emergency Response,
          // Structures, Metadata, Campaign Designer (placeholder).
          // Pillar type derived from entity_type — org_* communities use
          // the org_community pillar; geo_* use geo_community.
          <CommunityManagementTab
            communityGuid={communityGuid}
            pillarType={
              // Org classification must match the canonical predicate used by
              // playbookEligibility (et === 'organization' || et starts 'org_').
              // Rotary International is typed 'organization', not 'org_*', so a
              // startsWith-only check wrongly classified it as geo and hid the
              // org-only sub-tabs (Structures / Metadata / Playbooks). DEV-1277.
              (() => {
                const et = String(community?.entity_type || '').toLowerCase();
                return et === 'organization' || et.startsWith('org_')
                  ? 'org_community'
                  : 'geo_community';
              })()
            }
            isAdmin={canOpenSettings}
            communityName={String(community?.name || '')}
            currentUserGuid={currentUserGuid}
          />
        )}
      </div>

      {/* DEV-1414 — Request-to-join dialog. Opaque per the CLAUDE.md overlay
          rule (solid base + elevated tint composite). */}
      {joinOpen && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => !joinBusy && setJoinOpen(false)}
        >
          <div
            style={{
              backgroundColor: 'var(--bg-primary)',
              backgroundImage: 'linear-gradient(var(--bg-elevated), var(--bg-elevated))',
              border: '1px solid var(--border-default)', borderRadius: 12,
              padding: 20, width: 420, maxWidth: '90vw',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 6px', color: 'var(--text-primary)', fontSize: 17 }}>
              {t('communityDetail.joinModalTitle', { name: String(community?.name || '') })}
            </h3>
            <p style={{ margin: '0 0 12px', color: 'var(--text-muted)', fontSize: 13 }}>
              {t('communityDetail.joinModalHint')}
            </p>
            <label style={{ display: 'block', color: 'var(--text-muted)', fontSize: 12, marginBlockEnd: 4 }}>
              {t('communityDetail.joinMessageLabel')}
            </label>
            <textarea
              rows={3}
              value={joinMessage}
              onChange={(e) => setJoinMessage(e.target.value)}
              style={{
                width: '100%', boxSizing: 'border-box',
                background: 'var(--bg-surface)', border: '1px solid var(--border-default)',
                color: 'var(--text-primary)', borderRadius: 8, padding: '8px 10px',
                fontSize: 13, fontFamily: 'inherit',
              }}
            />
            <div style={{ display: 'flex', gap: 8, marginBlockStart: 14, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setJoinOpen(false)}
                disabled={joinBusy}
                style={{
                  background: 'var(--bg-surface)', color: 'var(--text-primary)',
                  border: '1px solid var(--border-default)', borderRadius: 8,
                  padding: '8px 14px', fontSize: 13, cursor: 'pointer',
                }}
              >
                {t('communityDetail.joinCancel')}
              </button>
              <button
                type="button"
                onClick={submitJoinRequest}
                disabled={joinBusy}
                style={{
                  background: 'var(--accent-primary)', color: '#fff',
                  border: 'none', borderRadius: 8, padding: '8px 14px',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  opacity: joinBusy ? 0.6 : 1,
                }}
              >
                {t('communityDetail.joinSubmit')}
              </button>
            </div>
          </div>
        </div>
      )}

      <CreateWorkspaceModal
        isOpen={createWorkspaceOpen}
        onClose={() => setCreateWorkspaceOpen(false)}
        communityGuid={communityGuid}
        currentUserGuid={currentUserGuid || ""}
        onCreated={() => setWorkspaceRefreshKey((k) => k + 1)}
        defaultScope="community"
      />
      <CommunitySettingsModal isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} currentUserGuid={currentUserGuid} communityGuid={communityGuid} />

      {/* DEV-895 / DEV-981 v2 — Circle crop modal for the community logo.
          Renders its own fixed-position backdrop with high z-index. Mirrors
          UserProfilePage's mount at the page level. */}
      {logoCrop && (
        <CircleCropModal
          imageSrc={logoCrop.src}
          naturalW={logoCrop.w}
          naturalH={logoCrop.h}
          onConfirm={handleLogoCropConfirm}
          onCancel={handleLogoCropCancel}
        />
      )}
    </div>
  );
};

export default CommunityDetailPage;
